import React, { useEffect, useState } from "react";
import Container from "./container";

import { FormContext, iControlProps } from "../types";
import { replacePunctuationCharacters } from "../helper";
import { TextInputV2 } from "@boi/react-components";

export type TextInputProps = React.DOMAttributes<HTMLElement> &
    iControlProps & {
        readOnly?: boolean;
        disabled?: boolean;
        placeholder?: string;
        minLength?: number;
        maxLength?: number;
        numbersOnly?: boolean;
        inputMode?: string;
        fieldWidth?: string;
        errorMessage?: string;
        field_boi_text_as_number?: boolean;
        type?: string;
        id?: number;
        field_clone_display?: string;
        field_clone_display_source?: string;
        context?: FormContext;
        error?: string;
        showError?: boolean;
    };

const useMutationObserver = (
    ref: any,
    callback: any,
    options = {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true
    }
) => {
    React.useEffect(() => {
        if (ref.current) {
            const observer = new MutationObserver(callback);
            observer.observe(ref.current, options);
            return () => observer.disconnect();
        }
    }, [callback, options]);
};

const TextInput = (props: TextInputProps) => {
    const showError = props.showError || true;
    const { cssClass, onChange, field_boi_text_as_number } = props;
    const cloneSource = props.context?.getControl(
        `input_${props.field_clone_display_source}`
    );
    const cloneSourceValue =
        (cloneSource && props.context?.getValue(cloneSource.name)) || "";

    const [value, setValue] = useState(props.value || "");

    useEffect(() => {
        let cloneValue = cloneSourceValue;
        if (
            props.field_clone_display &&
            props.field_clone_display_source &&
            props.context &&
            cloneSourceValue
        ) {
            if (typeof cloneSourceValue === "object") {
                if (cloneSourceValue.value) {
                    cloneValue = cloneSourceValue.value;
                } else {
                    cloneValue = Object.values(cloneSourceValue).join(", ");
                }
            }
            setValue(cloneValue);
            onChange && onChange(cloneValue);
        }
    }, [cloneSourceValue]);

    useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    const inputRef = React.useRef<HTMLInputElement>(null);
    cssClass?.includes("honeypot") &&
        useMutationObserver(inputRef, (mutations: MutationRecord[]) => {
            mutations.forEach((mutation: MutationRecord) => {
                if (
                    mutation.type === "attributes" &&
                    mutation.attributeName === "value"
                )
                    onChange && onChange(inputRef.current?.value);
            });
        });

    const symbol = (() => {
        if (!cssClass) return "";

        if (cssClass.includes("u-percentage")) {
            return "%";
        }

        if (cssClass.includes("u-currency-sterling")) {
            return "£";
        }

        if (
            cssClass.includes("u-currency-euro") ||
            cssClass.includes("u-currency")
        ) {
            return "€";
        }

        return "";
    })();

    const accessibilitySettings: { [key: string]: string } = {};
    props.accessibilitySettings &&
        props.accessibilitySettings.forEach(setting => {
            if (setting.isShown)
                accessibilitySettings[setting.name] = setting.value || "";
        });

    const inputClasses = props.cssClass
        ?.split(" ")
        .filter(
            (cls: string) => !cls.includes("u-pl-") && !cls.includes("u-ml-")
        )
        .join(" ");

    return (
        <Container {...props}>
            <TextInputV2
                value={value}
                placeholder={props.placeholder || ""}
                ref={inputRef}
                startSlot={symbol ?? null}
                isInvalid={!!props.error && showError}
                onChange={(e: string) => {
                    setValue(e);
                    onChange && onChange(replacePunctuationCharacters(e));
                }}
                onBlur={() => {
                    onChange && onChange(replacePunctuationCharacters(value));
                }}
                defaultValue={value}
                name={props.name}
                isDisabled={
                    props.disabled ||
                    props.cssClass?.includes("disabled") ||
                    !!props.field_clone_display
                }
                isReadOnly={
                    props.readOnly || props.cssClass?.includes("readonly")
                }
                isRequired={props.isRequired}
                inputMode={field_boi_text_as_number ? "numeric" : "text"}
                type="text"
                id={"" + props.id}
                className={inputClasses + " react-aria-TextField"}
                // remove this aria-label if the Container component has deleted and the label and other props are passed to TextInputV2
                aria-label={props.label}
                {...accessibilitySettings}
            />
        </Container>
    );
};

export default TextInput;
